import React, { useEffect, useMemo, useState } from "react";
import "./sharedCatalogue.css";
import notifications from "../../../common/Components/Notifications/Notification";
import { studioJwtAuthorizationValue } from "./publicCatalogueHelpers";
import {
  attachVisitorFromCatalogueSession,
  parseVisitorAuthPayload,
  type VisitorAuthPayload,
} from "./catalogueVisitorClient";
import type { PublicCatalogueStatus } from "../../../types/catalogue.types";
import {
  generateCatalogueViewerSession,
  sendCatalogueViewerOtp,
  verifyCatalogueViewerOtp,
} from "../../../components/catalogue/catalogueSecurityApi";
import {
  CATALOGUE_SHARE_COUNTRIES,
  DEFAULT_SHARE_COUNTRY_ISO,
  inviteCountryIso,
  isPlausibleSharePhone,
  isValidShareEmail,
  whatsappDigits,
} from "../../../components/catalogue/catalogueShareCountries";
import { catalogueUnlockProfile } from "../../../components/catalogue/catalogueUnlockMethods";

export interface PublicCatalogueAuthGateProps {
  catalogueId: string;
  catalogueTitle?: string;
  unlockStatus?: PublicCatalogueStatus | null;
  onUnlocked: (
    sessionToken: string,
    guest?: { displayName?: string; phone?: string },
    visitor?: VisitorAuthPayload
  ) => void;
  /** Who sent the invite, as in "Ask {hostLabel} for a new link". A reseller's client never sees the studio. */
  hostLabel?: string;
}

const PublicCatalogueAuthGate: React.FC<PublicCatalogueAuthGateProps> = ({
  catalogueId,
  catalogueTitle,
  unlockStatus,
  onUnlocked,
  hostLabel = "the studio",
}) => {
  const lockedCountryIso = inviteCountryIso(unlockStatus?.recipientCountryCode);
  const [phone, setPhone] = useState("");
  const [countryIso, setCountryIso] = useState(
    () => lockedCountryIso || DEFAULT_SHARE_COUNTRY_ISO
  );
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [useOtp, setUseOtp] = useState(false);

  const linkToken = new URLSearchParams(window.location.search).get("link") || undefined;
  const studioSignedIn = Boolean(studioJwtAuthorizationValue());
  const profile = useMemo(() => catalogueUnlockProfile(unlockStatus), [unlockStatus]);

  useEffect(() => {
    if (lockedCountryIso) setCountryIso(lockedCountryIso);
  }, [lockedCountryIso]);

  useEffect(() => {
    setUseOtp(profile?.primary === "otp");
    setOtp("");
    setOtpSent(false);
  }, [profile]);

  const applySession = async (data: {
    sessionToken?: string;
    recipientName?: string;
    visitorToken?: string;
    visitor?: unknown;
  }) => {
    if (!data?.sessionToken) return false;
    const recipientName = typeof data.recipientName === "string" ? data.recipientName.trim() : "";
    let visitor = parseVisitorAuthPayload(data);
    if (!visitor && data.sessionToken) {
      visitor = await attachVisitorFromCatalogueSession(catalogueId, data.sessionToken);
    }
    notifications({
      title: "Verified",
      message: "You can now view this collection.",
      type: "success",
    });
    onUnlocked(
      data.sessionToken,
      {
        displayName: recipientName || unlockStatus?.recipientName || undefined,
        phone: profile?.identity === "phone" ? whatsappDigits(countryIso, phone) : undefined,
      },
      visitor || undefined
    );
    return true;
  };

  const requireLink = (): boolean => {
    if (linkToken) return true;
    notifications({
      title: "Secure invite required",
      message: "Please open the secured catalogue link shared by your host.",
      type: "warning",
    });
    return false;
  };

  const requireIdentity = (): boolean => {
    if (!requireLink()) return false;
    if (profile?.identity === "email") {
      if (!isValidShareEmail(emailHint)) {
        notifications({
          title: "Email",
          message: "Enter the email this invite was sent to.",
          type: "warning",
        });
        return false;
      }
      return true;
    }
    if (!isPlausibleSharePhone(phone, countryIso)) {
      notifications({
        title: "Mobile",
        message: "Enter the mobile number this invite was shared with.",
        type: "warning",
      });
      return false;
    }
    return true;
  };

  const identityPayload = () =>
    profile?.identity === "email"
      ? { email: emailHint.trim() }
      : { phone, countryCode: countryIso };

  const sendOtp = async () => {
    if (!requireIdentity() || !profile?.otpChannel) return;
    setSendBusy(true);
    try {
      const json = await sendCatalogueViewerOtp(catalogueId, {
        linkToken: linkToken!,
        channel: profile.otpChannel,
        ...identityPayload(),
      });
      if (json?.success) {
        setOtpSent(true);
        const emailed = Boolean(json.data?.emailSent);
        const smsed = Boolean(json.data?.smsSent);
        notifications({
          title: emailed ? "Code emailed" : "OTP sent",
          message:
            json.message ||
            (emailed
              ? `Check ${json.data?.maskedEmail || "your email"} for the code.`
              : smsed
              ? "Check your SMS for the code."
              : "Check for the one-time code."),
          type: "success",
        });
      } else {
        notifications({
          title: "OTP",
          message: json?.message || "Could not send the code.",
          type: "danger",
        });
      }
    } catch {
      notifications({
        title: "Error",
        message: "Could not send the code. Try again.",
        type: "danger",
      });
    } finally {
      setSendBusy(false);
    }
  };

  const verifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requireIdentity()) return;
    if (!otp.trim()) {
      notifications({
        title: "OTP",
        message: "Enter the code you received.",
        type: "warning",
      });
      return;
    }
    setVerifyBusy(true);
    try {
      const json = await verifyCatalogueViewerOtp(catalogueId, {
        linkToken: linkToken!,
        otp: otp.trim(),
        ...identityPayload(),
      });
      if (json?.success && json.data) {
        const ok = await applySession(json.data);
        if (!ok) {
          notifications({
            title: "OTP",
            message: "Verified, but no session was returned. Try again.",
            type: "danger",
          });
        }
      } else {
        notifications({
          title: "OTP",
          message: json?.message || "Invalid or expired code.",
          type: "danger",
        });
      }
    } catch {
      notifications({
        title: "Error",
        message: "Something went wrong. Try again.",
        type: "danger",
      });
    } finally {
      setVerifyBusy(false);
    }
  };

  const unlockWithPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requireIdentity()) return;
    if (!password.trim()) {
      notifications({
        title: "Password",
        message: `Enter the password ${hostLabel} sent with this invite.`,
        type: "warning",
      });
      return;
    }
    setVerifyBusy(true);
    try {
      const json = await generateCatalogueViewerSession(catalogueId, {
        linkToken: linkToken!,
        password: password.trim(),
        ...identityPayload(),
      });
      if (json?.success && json.data) {
        const ok = await applySession(json.data);
        if (!ok) {
          notifications({
            title: "Password",
            message: "Could not start a session. Try again.",
            type: "danger",
          });
        }
      } else {
        notifications({
          title: "Password",
          message:
            json?.message ||
            (profile?.identity === "email"
              ? "Wrong email or password. This catalogue stays locked."
              : "Wrong phone or password. This catalogue stays locked."),
          type: "danger",
        });
      }
    } catch {
      notifications({
        title: "Error",
        message: "Something went wrong. Try again.",
        type: "danger",
      });
    } finally {
      setVerifyBusy(false);
    }
  };

  const greeting = unlockStatus?.recipientName
    ? `Hello ${unlockStatus.recipientName}. `
    : "";
  const emailIdentity = profile?.identity === "email";
  const showOtp = Boolean(useOtp && profile?.otpChannel);
  const otpChannel = profile?.otpChannel;

  const identityFields = emailIdentity ? (
    <>
      <label htmlFor="hkp-gate-email">Email</label>
      <input
        id="hkp-gate-email"
        type="email"
        autoComplete="email"
        value={emailHint}
        onChange={(e) => setEmailHint(e.target.value)}
        placeholder={unlockStatus?.maskedEmail || "Invited email"}
        aria-label="Invited email"
      />
    </>
  ) : (
    <>
      <label htmlFor="hkp-gate-country">Country</label>
      <div className="hkp-shared-gate-phone-row">
        <select
          id="hkp-gate-country"
          className="hkp-shared-gate-country"
          value={countryIso}
          onChange={(e) => setCountryIso(e.target.value)}
          disabled={Boolean(lockedCountryIso)}
          aria-label="Country code"
        >
          {CATALOGUE_SHARE_COUNTRIES.map((country) => (
            <option key={country.iso} value={country.iso}>
              {country.iso} +{country.dial}
            </option>
          ))}
        </select>
        <input
          id="hkp-gate-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={countryIso === "IN" ? "10-digit Indian mobile" : "Local mobile"}
          aria-label="Mobile number"
        />
      </div>
    </>
  );

  return (
    <div className="hkp-shared-gate">
      <h2>Unlock this collection</h2>
      <p className="hkp-shared-gate-lede">
        {greeting}
        {catalogueTitle
          ? `“${catalogueTitle}” is shared only with invited guests.`
          : "This collection is shared only with invited guests."}{" "}
        {emailIdentity
          ? "Verify using the email this invite was sent to."
          : "Verify using the mobile number this invite was shared with."}
      </p>

      {!linkToken && (
        <p className="hkp-shared-gate-hint" role="alert">
          This page opens only from a secured invite link. Ask your host to share the catalogue again.
        </p>
      )}
      {linkToken && unlockStatus?.linkValid === false && (
        <p className="hkp-shared-gate-hint" role="alert">
          This invite is expired or disabled. Ask {hostLabel} for a new link.
        </p>
      )}
      {studioSignedIn && (
        <p className="hkp-shared-gate-hint">
          Studio login detected. You can verify with preview code 1234 for this catalogue, or use the
          invited {emailIdentity ? "email" : "number"}.
        </p>
      )}

      {!profile && linkToken && unlockStatus?.linkValid !== false && (
        <p className="hkp-shared-gate-hint" role="alert">
          This invite cannot be unlocked yet. Ask {hostLabel} for a password or an email on the invite.
        </p>
      )}

      {profile && !showOtp && (
        <form onSubmit={unlockWithPassword}>
          {identityFields}
          <label htmlFor="hkp-gate-password">Invite password</label>
          <input
            id="hkp-gate-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={`Password from ${hostLabel}`}
          />
          <button
            type="submit"
            className="hkp-shared-gate-submit"
            disabled={verifyBusy || !linkToken}
          >
            {verifyBusy ? "Unlocking…" : "Unlock with password"}
          </button>
        </form>
      )}

      {profile && showOtp && otpChannel && (
        <form onSubmit={verifyOtp}>
          {identityFields}
          {otpChannel === "email" && (
            <p className="hkp-shared-gate-hint" style={{ marginTop: 0 }}>
              We’ll send a code to {unlockStatus?.maskedEmail || "the invited email"}.
            </p>
          )}
          <button
            type="button"
            className="hkp-shared-gate-secondary"
            onClick={() => void sendOtp()}
            disabled={sendBusy || !linkToken}
          >
            {sendBusy
              ? "Sending…"
              : otpSent
              ? otpChannel === "email"
                ? "Resend email code"
                : "Resend SMS code"
              : otpChannel === "email"
              ? "Send code to email"
              : "Send SMS code"}
          </button>
          <label htmlFor="hkp-gate-otp">One-time code</label>
          <input
            id="hkp-gate-otp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder={
              studioSignedIn
                ? otpChannel === "sms"
                  ? "SMS code or 1234"
                  : "Email code or 1234"
                : otpChannel === "email"
                ? "Enter email code"
                : "Enter SMS code"
            }
          />
          <button
            type="submit"
            className="hkp-shared-gate-submit"
            disabled={verifyBusy || !linkToken}
          >
            {verifyBusy ? "Verifying…" : "Verify and continue"}
          </button>
        </form>
      )}

      {profile?.otpChannel && profile.primary === "password" && !showOtp && (
        <button
          type="button"
          className="hkp-shared-linkish hkp-shared-gate-alt"
          onClick={() => {
            setUseOtp(true);
            setOtp("");
          }}
        >
          {profile.otpChannel === "email"
            ? "Email me a one-time code instead"
            : "Send SMS code instead"}
        </button>
      )}
      {profile?.primary === "password" && showOtp && (
        <button
          type="button"
          className="hkp-shared-linkish hkp-shared-gate-alt"
          onClick={() => {
            setUseOtp(false);
            setOtp("");
          }}
        >
          Use password instead
        </button>
      )}

      <p className="hkp-shared-gate-hint">
        Secure access is valid for this browser session (up to 72 hours). If you
        received a new invite, open that link directly.
      </p>
    </div>
  );
};

export default PublicCatalogueAuthGate;
